<template>
<div class="outer_legal_container">
    <!-- Always render the container, but conditionally show content -->
    <template v-if="data != null">
        <div class="cb_container">
            <v-checkbox
                v-model="cb_value"
                v-on:change="onChangeCheckBox"
                :required="required"
                aria-labelledby="legal_agreement_label"
                :disabled="data.allow_signature == false"
            />
        </div>
        <div
            id="legal_agreement_label"
            :class="{'label_container': true, 'label_container_disabled': !data.allow_signature}"
            v-on:click="onClickLabel"
        >
            <span
                v-for="(item, index) in data.segments"
                :key="'seg_'+index"
            >
                <a
                    v-if="item.type == 'link'"
                    @click.stop
                    :href="item.url"
                    target="_blank"
                >{{item.text}}</a>
                <span
                    v-else
                >
                    {{item.text}}
                </span>
            </span>
        </div>
    </template>
    <!-- Show loading state when data is being fetched -->
    <template v-else-if="is_loading">
        <div class="loading-container">
            <v-progress-circular
                indeterminate
                color="primary"
                size="20"
                width="2"
            ></v-progress-circular>
            <span class="loading-text">Loading agreement...</span>
        </div>
    </template>
</div>
</template>

<script>
const _ = require('lodash');

export default {
    props: {
        app_id: {
            type: String,
            default: function() { return null; }
        },

        type: {
            type: String,
            default: function() { return 'bulk-message-attestation'; }
        },

        organization_id: {
            type: String,
            default: function() { return null; }
        },

        organization_label: {
            type: String,
            default: function() { return null; }
        },

        override_patterns: {
            type: Array,
            default: function() { return null; }
        },

        required: {
            type: Boolean,
            default: function() { return false; }
        },
    },

    data() {
        return {
            cb_value: false,
            data: null,
            retry_count: 0,
            max_retries: 3,
            retry_timeout: null,
            api_timeout_id: null,
            is_loading: false,
        };
    },

    mounted () {
        this.cb_value = false;
        this.doUpdate();
    },

    methods: {
        update: _.debounce(function(){
            this.retry_count = 0;
            if (this.retry_timeout) {
                clearTimeout(this.retry_timeout);
                this.retry_timeout = null;
            }
            this.doUpdate();
        }, 200),

        doUpdate() {
            // Don't attempt to fetch if organization_id is empty or null
            if (!this.organization_id || this.organization_id === '') {
                if (this.retry_count < this.max_retries) {
                    this.retry_count++;
                    this.retry_timeout = setTimeout(() => this.doUpdate(), 200);
                }
                return;
            }

            // Set loading state to true
            this.is_loading = true;

            // Set a timeout to handle cases where the API call hangs
            this.api_timeout_id = setTimeout(() => {
                if (this.is_loading) {
                    this.is_loading = false;
                    if (this.retry_count < this.max_retries) {
                        this.retry_count++;
                        this.retry_timeout = setTimeout(() => this.doUpdate(), 200);
                    }
                }
            }, 100); 

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/legal/get-agreement',
                'params': {
                    'type': this.type,
                    'organization_id': this.organization_id,
                    'organization_label': this.organization_label,
                    'override_patterns': this.override_patterns,
                    'app_id': this.app_id,
                },
                'on_success': (data) => {
                    clearTimeout(this.api_timeout_id);
                    this.$nextTick(() => {
                        this.data = data;
                        this.retry_count = 0;
                        this.is_loading = false;
                    });
                },
                'on_error': () => {
                    clearTimeout(this.api_timeout_id);
                    this.cb_value = false;
                    this.is_loading = false;

                    if (this.retry_count < this.max_retries) {
                        this.retry_count++;
                        this.retry_timeout = setTimeout(() => this.doUpdate(), 200);
                    }
                },
            });
        },

        onClickLabel()
        {
            if ( this.data.allow_signature == false )
            {
                this.$rc.alert('Unable to Sign Agreement', this.data.allow_signature_explanation);
                return;
            }

            this.cb_value = !this.cb_value;
            this.$emit('input',this.cb_value);
        },

        onChangeCheckBox()
        {
            this.$emit('input',this.cb_value);
        },

        getSpecimen()
        {
            return this.data?.plain_text_specimen || null;
        },
    },

    beforeDestroy() {
        // Clean up any pending timeouts
        if (this.retry_timeout) {
            clearTimeout(this.retry_timeout);
            this.retry_timeout = null;
        }
        if (this.api_timeout_id) {
            clearTimeout(this.api_timeout_id);
            this.api_timeout_id = null;
        }
    },

    watch: {
        organization_id(new_value, old_value) {
            if (new_value && new_value !== old_value) {
                this.update();
            }
        },

        organization_label(new_value, old_value) {
            if (new_value && new_value !== old_value) {
                this.update();
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.outer_legal_container
{
    display: flex;
    align-items: stretch;
    min-height: 56px; /* Ensure consistent height even when loading */
}

.cb_container
{
    flex-grow: 0;
}

.label_container
{
    flex-grow: 1;
    padding-top: 19px;
    font-size: 16px;
    color: rgba(0,0,0,0.6);
    cursor: pointer;
}

.label_container_disabled
{
    color: rgba(0,0,0,0.2);
}

.label_container_disabled a
{
    color: rgba(0,0,0,0.2);
}

.loading-container {
    display: flex;
    align-items: center;
    padding: 16px 0;
    width: 100%;
}

.loading-text {
    margin-left: 12px;
    color: rgba(0,0,0,0.6);
    font-size: 14px;
}
</style>